/**
 * Education page scripts.
 * Splide is enqueued only on the Education template.
 *
 * @package Naari
 */

(function () {
	'use strict';

	function initEducationServices(root) {
		if (!root) return;

		var sections = Array.prototype.slice.call(root.querySelectorAll('[data-edu-services][data-season]'));
		if (!sections.length) return;

		function setActiveCategory(section, slug) {
			var cats = section.querySelectorAll('.edu-s2__cat[data-category]');
			cats.forEach(function (btn) {
				var active = btn.getAttribute('data-category') === slug;
				btn.classList.toggle('is-active', active);
				btn.setAttribute('aria-selected', active ? 'true' : 'false');
			});
		}

		function setBodyVisible(section, visible) {
			var body = section.querySelector('.edu-s2__body');
			if (!body) return;
			if (visible) body.removeAttribute('hidden');
			else body.setAttribute('hidden', '');
		}

		function setActivePanel(section, slug) {
			var panels = section.querySelectorAll('.edu-s2__panel[data-category]');
			panels.forEach(function (panel) {
				var isActive = panel.getAttribute('data-category') === slug;
				panel.hidden = !isActive;
			});
		}

		function resetPanelCollapsed(panel) {
			if (!panel) return;
			var items = panel.querySelectorAll('.edu-s2-item');
			items.forEach(function (item, i) {
				item.classList.toggle('edu-s2-item--hidden', i >= 3);
			});
			var wrap = panel.querySelector('.edu-s2__loadmore-wrap');
			var btn = panel.querySelector('.edu-s2__loadmore');
			if (wrap) {
				if (items.length > 3) wrap.removeAttribute('hidden');
				else wrap.setAttribute('hidden', '');
			}
			if (btn) {
				btn.style.display = '';
				btn.setAttribute('aria-expanded', 'false');
			}
		}

		function expandPanel(panel) {
			if (!panel) return;
			var hiddenItems = panel.querySelectorAll('.edu-s2-item.edu-s2-item--hidden');
			hiddenItems.forEach(function (el, i) {
				setTimeout(function () {
					el.classList.remove('edu-s2-item--hidden');
				}, i * 60);
			});
			var btn = panel.querySelector('.edu-s2__loadmore');
			if (btn) {
				btn.setAttribute('aria-expanded', 'true');
				btn.style.display = 'none';
			}
		}

		function initSection(section) {
			if (section.getAttribute('data-edu-services-init') === 'true') {
				return;
			}

			var firstCatBtn = section.querySelector('.edu-s2__cat[data-category]');
			if (!firstCatBtn) return;

			// Default state: body hidden, no active category, all panels hidden.
			setBodyVisible(section, false);
			setActiveCategory(section, '');
			setActivePanel(section, '__none__');

			section.addEventListener('click', function (e) {
				var catBtn = e.target && e.target.closest ? e.target.closest('.edu-s2__cat[data-category]') : null;
				if (catBtn && section.contains(catBtn)) {
					var slug = catBtn.getAttribute('data-category') || '';
					if (!slug) return;
					e.preventDefault();
					setActiveCategory(section, slug);
					setBodyVisible(section, true);
					setActivePanel(section, slug);

					var activePanel = section.querySelector('.edu-s2__panel[data-category="' + slug + '"]');
					resetPanelCollapsed(activePanel);
					return;
				}

				var loadBtn = e.target && e.target.closest ? e.target.closest('.edu-s2__loadmore') : null;
				if (loadBtn && section.contains(loadBtn)) {
					e.preventDefault();
					var currentSlug = loadBtn.getAttribute('data-category') || '';
					var panel = section.querySelector('.edu-s2__panel[data-category="' + currentSlug + '"]');
					expandPanel(panel);
				}
			});

			section.setAttribute('data-edu-services-init', 'true');
		}

		sections.forEach(function (section) {
			initSection(section);
		});
	}

	/**
	 * Splide: “Words she shares” sliders live inside each tab panel. Lazy-mount on first
	 * visible view; refresh layout after tab switch (hidden panels report zero width).
	 *
	 * @param {HTMLElement | null} panel Tab panel element (`[data-edu-panel]`).
	 */
	function mountOrRefreshWordsSplide(panel) {
		if (!panel) return;
		var el = panel.querySelector('[data-edu-words-splide]');
		if (!el || typeof window.Splide === 'undefined') return;

		if (el._eduWordsSplide) {
			window.requestAnimationFrame(function () {
				try {
					el._eduWordsSplide.refresh();
				} catch (e) {
					// Splide may throw if DOM was removed; ignore.
				}
			});
			return;
		}

		var splide = new window.Splide(el, {
			type: 'slide',
			perPage: 1,
			perMove: 1,
			gap: '2rem',
			arrows: true,
			pagination: false,
			speed: 600,
			breakpoints: {
				1024: {
					gap: '2.5rem',
				},
				767: {
					gap: '1.5rem',
				},
			},
			i18n: {
				prev: 'Previous slide',
				next: 'Next slide',
			},
		});
		splide.mount();
		el._eduWordsSplide = splide;
	}

	/**
	 * Inner tabs: “Packages” duration tabs live inside each panel (per life stage).
	 *
	 * @param {HTMLElement | null} root A visible panel element (or `.edu` root).
	 */
	function initEducationPackages(root) {
		if (!root) return;

		var sections = Array.prototype.slice.call(root.querySelectorAll('[data-edu-packages]'));
		if (!sections.length) return;

		function setActive(section, duration, focusTab) {
			if (!section || !duration) return;

			var tabs = Array.prototype.slice.call(section.querySelectorAll('[data-edu-packages-tab][data-duration]'));
			var panels = Array.prototype.slice.call(section.querySelectorAll('[data-edu-packages-panel][data-duration]'));
			if (!tabs.length || !panels.length) return;

			tabs.forEach(function (tab) {
				var isActive = tab.getAttribute('data-duration') === duration;
				tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
				tab.setAttribute('tabindex', isActive ? '0' : '-1');
				if (isActive && focusTab) tab.focus();
			});

			panels.forEach(function (panel) {
				panel.hidden = panel.getAttribute('data-duration') !== duration;
			});
		}

		function findActiveIndex(tabs) {
			for (var i = 0; i < tabs.length; i++) {
				if (tabs[i].getAttribute('aria-selected') === 'true') return i;
			}
			return 0;
		}

		sections.forEach(function (section) {
			if (section.getAttribute('data-edu-packages-init') === 'true') return;

			var tabs = Array.prototype.slice.call(section.querySelectorAll('[data-edu-packages-tab][data-duration]'));
			if (!tabs.length) return;

			var defaultDuration = tabs[0].getAttribute('data-duration') || '';
			if (defaultDuration) setActive(section, defaultDuration, false);

			section.addEventListener('click', function (e) {
				var tab = e.target && e.target.closest ? e.target.closest('[data-edu-packages-tab][data-duration]') : null;
				if (!tab || !section.contains(tab)) return;
				e.preventDefault();
				var duration = tab.getAttribute('data-duration') || '';
				if (!duration) return;
				setActive(section, duration, false);
			});

			section.addEventListener('keydown', function (e) {
				var tab = e.target && e.target.closest ? e.target.closest('[data-edu-packages-tab][data-duration]') : null;
				if (!tab || !section.contains(tab)) return;

				var key = e.key || '';
				if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Home' && key !== 'End' && key !== 'Enter' && key !== ' ') {
					return;
				}

				var currentTabs = Array.prototype.slice.call(section.querySelectorAll('[data-edu-packages-tab][data-duration]'));
				var currentIndex = findActiveIndex(currentTabs);
				var nextIndex = currentIndex;

				if (key === 'ArrowRight') nextIndex = Math.min(currentTabs.length - 1, currentIndex + 1);
				if (key === 'ArrowLeft') nextIndex = Math.max(0, currentIndex - 1);
				if (key === 'Home') nextIndex = 0;
				if (key === 'End') nextIndex = currentTabs.length - 1;

				if (nextIndex !== currentIndex) {
					e.preventDefault();
					currentTabs[nextIndex].focus();
					return;
				}

				if (key === 'Enter' || key === ' ') {
					e.preventDefault();
					var duration = tab.getAttribute('data-duration') || '';
					if (!duration) return;
					setActive(section, duration, false);
				}
			});

			section.setAttribute('data-edu-packages-init', 'true');
		});
	}

	/**
	 * Lottie: Section 1 background animation per life-stage tab.
	 * Lazy-mounts when the tab panel becomes visible (hidden panels have zero layout).
	 *
	 * @param {HTMLElement | null} panel Tab panel element (`[data-edu-panel]`).
	 */
	function mountEduS1Lottie(panel) {
		if (!panel) return;
		if (!window.lottie || typeof window.lottie.loadAnimation !== 'function') return;

		var el = panel.querySelector('[data-edu-lottie][data-lottie-path]');
		if (!el) return;

		function destroyExisting() {
			if (el._eduLottie && typeof el._eduLottie.destroy === 'function') {
				try {
					el._eduLottie.destroy();
				} catch (e) {
					// Ignore destroy errors.
				}
			}
			el._eduLottie = null;
			el.innerHTML = '';
		}

		if (el._eduLottie) {
			if (el.querySelector('svg')) {
				window.requestAnimationFrame(function () {
					try {
						el._eduLottie.resize();
						el._eduLottie.play();
					} catch (e) {
						// Animation may have been destroyed; ignore.
					}
				});
				return;
			}
			destroyExisting();
		}

		var path = el.getAttribute('data-lottie-path') || '';
		if (!path) return;

		var fit = el.getAttribute('data-lottie-fit') || 'slice';
		var preserveAspectRatio = fit === 'meet' ? 'xMidYMid meet' : 'xMidYMid slice';

		// Wait until the panel is visible and has layout (hidden panels report zero size).
		window.requestAnimationFrame(function () {
			window.requestAnimationFrame(function () {
				try {
					var anim = window.lottie.loadAnimation({
						container: el,
						renderer: 'svg',
						loop: true,
						autoplay: true,
						path: path,
						rendererSettings: {
							preserveAspectRatio: preserveAspectRatio,
						},
					});

					el._eduLottie = anim;

					anim.addEventListener('DOMLoaded', function () {
						try {
							anim.resize();
							anim.play();
						} catch (e) {
							// Ignore resize/play errors.
						}
					});

					anim.addEventListener('data_failed', function () {
						destroyExisting();
					});
				} catch (e) {
					// Ignore load failures (missing JSON, etc.).
				}
			});
		});
	}

	/**
	 * Start Section 1 video backgrounds when a tab becomes active.
	 *
	 * @param {HTMLElement | null} panel Tab panel element (`[data-edu-panel]`).
	 */
	function playEduS1Video(panel) {
		if (!panel) return;

		var video = panel.querySelector('.edu-s1__anim video.edu-s1__anim-media');
		if (!video) return;

		try {
			var playPromise = video.play();
			if (playPromise && typeof playPromise.catch === 'function') {
				playPromise.catch(function () {
					// Autoplay may be blocked until user interaction.
				});
			}
		} catch (e) {
			// Ignore playback errors.
		}
	}

	function initEducationTabs() {
		var root = document.querySelector('.edu');
		if (!root) return;

		var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"][data-edu-tab]'));
		var panels = Array.prototype.slice.call(root.querySelectorAll('[role="tabpanel"][data-edu-panel]'));
		if (!tabs.length || !panels.length) return;

		function getTabName(tab) {
			return tab && tab.getAttribute ? tab.getAttribute('data-edu-tab') : '';
		}

		function setActiveTabByName(name, focusTab) {
			if (!name) return;

			if (document.body) {
				document.body.setAttribute('data-edu-theme', name);
			}

			tabs.forEach(function (tab) {
				var isActive = getTabName(tab) === name;
				tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
				tab.setAttribute('tabindex', isActive ? '0' : '-1');
				if (isActive && focusTab) tab.focus();
			});

			panels.forEach(function (panel) {
				var isActive = panel.getAttribute('data-edu-panel') === name;
				if (isActive) {
					panel.removeAttribute('hidden');
				} else {
					panel.setAttribute('hidden', '');
				}
			});

			// Re-init services for the now-visible panel (independent per tab).
			var activePanel = root.querySelector('[role="tabpanel"][data-edu-panel="' + name + '"]');
			if (activePanel) {
				initEducationServices(activePanel);
				mountOrRefreshWordsSplide(activePanel);
				initEducationPackages(activePanel);
				mountEduS1Lottie(activePanel);
				playEduS1Video(activePanel);
			}
		}

		function findCurrentIndex() {
			for (var i = 0; i < tabs.length; i++) {
				if (tabs[i].getAttribute('aria-selected') === 'true') return i;
			}
			return 0;
		}

		tabs.forEach(function (tab) {
			tab.addEventListener('click', function () {
				setActiveTabByName(getTabName(tab), false);
			});

			tab.addEventListener('keydown', function (e) {
				var key = e.key || '';
				var currentIndex = findCurrentIndex();
				var nextIndex = currentIndex;

				if (key === 'ArrowRight') nextIndex = Math.min(tabs.length - 1, currentIndex + 1);
				if (key === 'ArrowLeft') nextIndex = Math.max(0, currentIndex - 1);
				if (key === 'Home') nextIndex = 0;
				if (key === 'End') nextIndex = tabs.length - 1;

				if (nextIndex !== currentIndex) {
					e.preventDefault();
					tabs[nextIndex].focus();
					return;
				}

				if (key === 'Enter' || key === ' ') {
					e.preventDefault();
					setActiveTabByName(getTabName(tab), false);
				}
			});
		});

		// Apply initial tab from server-rendered state (supports ?tab= query param).
		var initialTab = root.getAttribute('data-edu-initial-tab') || '';
		var defaultTab = getTabName(tabs[0]) || 'adolescence';
		var validTabs = tabs.map(getTabName).filter(Boolean);
		if (!initialTab || validTabs.indexOf(initialTab) === -1) {
			initialTab = defaultTab;
		}
		setActiveTabByName(initialTab, false);
	}

	function init() {
		var root = document.querySelector('.edu');
		initEducationTabs();
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init);
	} else {
		init();
	}
})();

